import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Fade, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { IdShortener } from '@vy/services';
import type { IntegrationProviders } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { isNotEmpty } from '@vy/array';
import { isError, isNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import type { UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import {
  useExportNetSuiteCreditMemo,
  useQuickBooksExportCreditMemo,
  useXeroExportCreditMemo,
} from '../../../../hooks/requests/integrations';
import { useDeleteCreditNote } from '../../../../hooks/requests/use-delete-credit-note';
import { useGetCreditNotePdf } from '../../../../hooks/use-get-credit-note-pdf';
import { useMenu } from '../../../../hooks/use-menu';
import { queryKeys } from '../../../../services/query-key/query-keys';
import { saveFileAsPdf } from '../../../../services/file-saver-as-pdf';
import { runMutation } from '../../../../utils/vy-mutation';
import { userMessage } from '../../../../utils/vy-toasts';
import { ShareCreditNoteMenu } from './ShareCreditNoteMenu';
import { ShareCreditNoteToSlackDialog } from './ShareCreditNoteToSlackDialog';
import { SyncCreditNoteMenu } from './SyncCreditNoteMenu';

type CreditNoteTableDotMenuProps = {
  creditLedgerId: string;
  customerId: string;
  erpId?: string;
  syncedIntegrations?: string[];
  connectedIntegrations: IntegrationProviders[];
  customerName: string;
  contactEmails: string[];
};

export const CreditNoteTableDotMenu: React.FC<CreditNoteTableDotMenuProps> = ({
  creditLedgerId,
  customerId,
  erpId,
  syncedIntegrations,
  connectedIntegrations,
  customerName,
  contactEmails,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const [isSlackDialogOpen, setIsSlackDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const downloadCreditNotePdfMutation = useGetCreditNotePdf();
  const deleteCreditNoteMutation = useDeleteCreditNote();

  const exportNetSuiteCreditMemoMutation = useExportNetSuiteCreditMemo();
  const exportXeroCreditMemoMutation = useXeroExportCreditMemo();
  const exportQuickBooksCreditMemoMutation = useQuickBooksExportCreditMemo();

  const isSyncDisabled = isNullOrUndefined(erpId);
  const isDeleteDisabled = isNotEmpty(syncedIntegrations);
  const isNetSuiteSyncDisabled = isSyncDisabled || syncedIntegrations?.includes(INTEGRATION_PROVIDERS.netSuite);
  const isXeroSyncDisabled = isSyncDisabled || syncedIntegrations?.includes(INTEGRATION_PROVIDERS.xero);
  const isQuickBooksSyncDisabled = isSyncDisabled || syncedIntegrations?.includes(INTEGRATION_PROVIDERS.quickBooks);

  const handleSyncTo = async (mutation: UseMutationResult<void, unknown, string>, providerName: string) => {
    await runMutation(
      mutation,
      creditLedgerId,
      {
        pending: `Exporting credit note to ${providerName}...`,
        success: `Credit note export to ${providerName} has been queued`,
        error: `Failed to export credit note to ${providerName}`,
      },
      {
        onError: (error) => {
          if (isError(error)) {
            userMessage('error', error.message);
          }
        },
      },
    );
  };

  const handleDelete = async () => {
    handleMenuClose();
    await runMutation(
      deleteCreditNoteMutation,
      creditLedgerId,
      {
        pending: 'Deleting credit note...',
        success: 'Credit note deleted',
        error: 'Failed to delete credit note',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(queryKeys.creditNotes(customerId));
          await queryClient.invalidateQueries(queryKeys.creditLedgerOverview(customerId));
        },
        onError: (error) => {
          if (isError(error)) {
            userMessage('error', error.message);
          }
        },
      },
    );
  };

  const handleDownloadPdf = async () => {
    handleMenuClose();
    await runMutation(
      downloadCreditNotePdfMutation,
      { creditLedgerId },
      {
        pending: 'Downloading credit note...',
        success: 'Credit note downloaded',
        error: 'Failed to download credit note',
      },
      {
        onSuccess: async ({ encodedPdf }) => {
          await saveFileAsPdf(encodedPdf, `CreditNote-${IdShortener(creditLedgerId)}`);
        },
      },
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleMenuOpen(event);
  };

  return (
    <>
      <IconButton onClick={handleMenuClick} data-vy="credit-note-dot-menu-button">
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        slots={{
          transition: Fade,
        }}
      >
        <MenuItem onClick={handleDownloadPdf} data-vy="credit-note-dot-menu-download">
          <ListItemIcon>
            <VyIcon icon="Download" color="black" style={{ transform: 'scale(0.7)' }} />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>

        <ShareCreditNoteMenu
          creditLedgerEntryId={creditLedgerId}
          customerName={customerName}
          contactEmails={contactEmails}
          handleParentMenuClose={handleMenuClose}
          onSlackClick={() => setIsSlackDialogOpen(true)}
        />

        <SyncCreditNoteMenu
          connectedIntegrations={connectedIntegrations}
          handleMenuClose={handleMenuClose}
          onSyncToNetSuite={() => handleSyncTo(exportNetSuiteCreditMemoMutation, 'NetSuite')}
          onSyncToXero={() => handleSyncTo(exportXeroCreditMemoMutation, 'Xero')}
          onSyncToQuickBooks={() => handleSyncTo(exportQuickBooksCreditMemoMutation, 'QuickBooks')}
          isSyncDisabled={isSyncDisabled}
          isNetSuiteSyncDisabled={isNetSuiteSyncDisabled}
          isXeroSyncDisabled={isXeroSyncDisabled}
          isQuickBooksSyncDisabled={isQuickBooksSyncDisabled}
        />

        <MenuItem
          disabled={isDeleteDisabled}
          onClick={handleDelete}
          data-vy="credit-note-dot-menu-delete"
        >
          <ListItemIcon>
            <VyIcon icon="Delete" color="black" style={{ transform: 'scale(0.7)' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <ShareCreditNoteToSlackDialog
        open={isSlackDialogOpen}
        onClose={() => setIsSlackDialogOpen(false)}
        creditLedgerEntryId={creditLedgerId}
      />
    </>
  );
};
